import { useQuery, useQueryClient } from '@tanstack/react-query';
import dashboardService from '../services/dashboardService';
import consola from "consola";

/**
 * Custom hook for dashboard overview data using React Query
 * Provides consistent caching with other dashboard modules
 */
export const useDashboardOverview = (organizationId, config = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["dashboard-overview", organizationId, config],
    queryFn: async () => {
      try {
        consola.debug(
          "Fetching overview data for orgId:",
          organizationId,
          "config:",
          config,
        );
        const result = await dashboardService.getOverviewData(
          organizationId,
          null,
          false,
          config,
        );
        consola.debug(
          "Overview data loaded via React Query - full result:",
          result,
        );
        consola.debug("Overview data - kpis:", result?.kpis);
        consola.debug("Overview data - activityData:", result?.activityData);

        // Ensure we have the expected data structure
        const processedResult = {
          kpis: result?.kpis || {},
          activityData: result?.activityData || [],
          growthData: result?.growthData || [],
          alerts: result?.alerts || [],
          quickActions: result?.quickActions || [],
          aiInsights: result?.aiInsights || [],
        };

        consola.debug("Processed overview result:", processedResult);
        return processedResult;
      } catch (error) {
        console.error("Error in overview queryFn:", error);
        throw error;
      }
    },
    enabled: !!organizationId,
    staleTime:            60 * 60 * 1000,
    gcTime:               4 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 3,
    // Removed initialData - it was preventing the actual API call
  });

  // Manual refresh function that bypasses cache
  const forceRefresh = async () => {
    // Clear React Query cache for this specific query
    await queryClient.invalidateQueries({
      queryKey: ["dashboard-overview", organizationId],
    });

    // Also clear dashboardService internal cache
    const cacheKey = `overview_${organizationId}_`;
    dashboardService.clearCache(cacheKey);

    // Trigger refetch
    return query.refetch();
  };

  return {
    ...query,
    forceRefresh,
    // Alias for consistency with existing code
    refreshing: query.isRefetching,
    loading: query.isLoading,
  };
};

/**
 * Custom hook for AI insights with separate caching
 * AI insights can be loaded independently and cached separately
 */
export const useAIInsights = (organizationId, dateRange) => {
  return useQuery({
    queryKey: ['ai-insights', organizationId, dateRange],
    queryFn: async () => {
      const startDate = dateRange.start.toISOString().split('T')[0];
      const endDate = dateRange.end.toISOString().split('T')[0];
      
      const result = await dashboardService.getAIInsights(startDate, endDate, false);
      consola.debug('AI insights loaded via React Query:', result);
      return result;
    },
    enabled: !!organizationId && !!dateRange.start && !!dateRange.end,
    staleTime: 30 * 60 * 1000, // 30 minutes - AI insights don't change as frequently
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    retry: 2, // Fewer retries for AI insights as they're less critical
    // Removed initialData to allow proper loading states
  });
};